import type { TestCaseRefDto } from '@tms/shared';
import { FileText } from 'lucide-react';
import { useState } from 'react';
import {
  Button,
  Dialog,
  EntityKey,
  SearchField,
  SkeletonRows,
} from '../../components/ui';
import { useT } from '../../i18n';
import { useDebounced } from '../../lib/use-debounced';
import { useTestCases } from '../test-cases/api';

export interface LinkCaseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectCode: string;
  onPick: (testCase: TestCaseRefDto) => void;
}

/**
 * Picks the test case a check covers.
 *
 * A project can hold hundreds of cases, so this searches the list by title and
 * key rather than offering a select of everything.
 */
export const LinkCaseDialog = ({
  open,
  onOpenChange,
  projectCode,
  onPick,
}: LinkCaseDialogProps) => {
  const { t } = useT();
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search);

  const { data, isPending } = useTestCases(
    projectCode,
    { perPage: 20, ...(debounced ? { q: debounced } : {}) },
    { enabled: open },
  );

  const rows = data?.data ?? [];

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('checklists.linkCaseTitle')}
      size="md"
      footer={
        <Button variant="secondary" onClick={() => onOpenChange(false)}>
          {t('common.cancel')}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="flex">
          <SearchField
            value={search}
            onChange={setSearch}
            placeholder={t('cases.searchPlaceholder')}
            label={t('cases.searchLabel')}
          />
        </div>

        {isPending ? (
          <SkeletonRows rows={3} className="p-0" />
        ) : rows.length === 0 ? (
          <p className="flex items-center gap-2 py-4 text-[13px] text-ink-subtle">
            <FileText className="size-4" />
            {t('checklists.linkCaseEmpty')}
          </p>
        ) : (
          <ul className="flex max-h-80 flex-col overflow-y-auto">
            {rows.map((testCase) => (
              <li key={testCase.id}>
                <button
                  type="button"
                  onClick={() => {
                    onPick({
                      id: testCase.id,
                      caseKey: testCase.caseKey,
                      title: testCase.title,
                    });
                    onOpenChange(false);
                  }}
                  className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-left hover:bg-sunken"
                >
                  <EntityKey className="shrink-0">{testCase.caseKey}</EntityKey>
                  <span className="min-w-0 flex-1 truncate text-[13px] text-ink">
                    {testCase.title}
                  </span>
                  <span className="shrink-0 text-[12px] text-ink-subtle">
                    {testCase.folderName}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  );
};
